import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import {Box, Stack, Typography} from '@mui/material';

// AD-2695: an answer is stored as JSON; a file answer keeps its name.
const formatAnswer = (raw) => {
    if (raw === null || raw === undefined || raw === '') return 'No answer';
    let value;
    try {
        value = JSON.parse(raw);
    } catch {
        return String(raw);
    }
    if (value === null || value === '') return 'No answer';
    if (Array.isArray(value)) return value.length ? value.map(v => v?.name ?? v?.label ?? String(v)).join(', ') : 'No answer';
    if (typeof value === 'object') return value.name ?? value.label ?? JSON.stringify(value);
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    return String(value);
};

const formatWhen = (date) => moment.utc(date).local().format('D MMMM YYYY [at] HH:mm');

// One save is one entry: the person who made it, when, and each answer before and after.
const groupBySave = (edits) => {
    const groups = [];
    edits.forEach(edit => {
        const key = `${edit.formRequestId}|${edit.editedBy}|${edit.editedAt}`;
        const last = groups[groups.length - 1];
        if (last && last.key === key) {
            last.edits.push(edit);
        } else {
            groups.push({key, editedBy: edit.editedBy, editedAt: edit.editedAt, edits: [edit]});
        }
    });
    return groups;
};

const ResponseEditHistory = ({edits, emptyText}) => {
    if (!edits?.length) {
        return emptyText ? <Typography variant="body2" color="text.secondary">{emptyText}</Typography> : null;
    }

    return (
        <Stack spacing={1.5} data-testid="response-edit-history">
            {groupBySave(edits).map(group => (
                <Box key={group.key} sx={{p: 1.5, border: '1px solid #e3e8ef', borderRadius: 2}}>
                    <Typography variant="subtitle2" sx={{fontWeight: 'bold'}}>
                        Edited by {group.editedBy} on {formatWhen(group.editedAt)}
                    </Typography>
                    {group.edits.map((edit, index) => (
                        <Box key={index} sx={{mt: 1}}>
                            <Typography variant="body2" sx={{fontWeight: 500}}>{edit.question}</Typography>
                            <Typography variant="body2" color="text.secondary">
                                Before: {formatAnswer(edit.before)}
                            </Typography>
                            <Typography variant="body2">After: {formatAnswer(edit.after)}</Typography>
                        </Box>
                    ))}
                </Box>
            ))}
        </Stack>
    );
};

ResponseEditHistory.propTypes = {
    edits: PropTypes.arrayOf(PropTypes.shape({
        formRequestId: PropTypes.number,
        editedBy: PropTypes.string,
        editedAt: PropTypes.string,
        question: PropTypes.string,
        before: PropTypes.string,
        after: PropTypes.string,
    })),
    emptyText: PropTypes.string,
};

export default ResponseEditHistory;
